import React from 'react';
import '../pages/GameRecap.css';

function formatNewspaperDate(dateStr) {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return String(dateStr);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  } catch {
    return String(dateStr);
  }
}

/**
 * Shared Gladiator Gazette newspaper layout (Live Scoreboard, TV popup, archive).
 * @param {{ recap: object, dateStr?: string, teamLabel?: string, className?: string, showShare?: boolean, footer?: React.ReactNode }} props
 */
export default function GazetteNewspaper({
  recap,
  dateStr,
  teamLabel = '',
  className = '',
  showShare = true,
  footer = null,
}) {
  if (!recap?.headline) return null;

  const formattedDate = formatNewspaperDate(dateStr || recap.generatedAt);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleShare = async () => {
    const shareText = `${recap.headline}\n\n${recap.body}\n\n${shareUrl}`;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: recap.headline, text: recap.body, url: shareUrl });
      } catch (e) {
        if (e.name !== 'AbortError') {
          try {
            await navigator.clipboard.writeText(shareText);
            alert('Copied to clipboard!');
          } catch {
            alert('Share not supported.');
          }
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        alert('Copied to clipboard!');
      } catch {
        alert('Copy failed. Select and copy the recap text above.');
      }
    }
  };

  return (
    <div className={`newspaper-recap-container ${className}`.trim()}>
      <div className="newspaper-recap-content">
        <div className="newspaper-masthead">
          <div className="newspaper-masthead-title">The Gladiator Gazette</div>
          <div className="newspaper-masthead-subtitle">
            Official Game Report{teamLabel ? ` · ${teamLabel}` : ''}
          </div>
        </div>
        {formattedDate && <div className="newspaper-date">{formattedDate}</div>}
        <h1 className="newspaper-headline">{recap.headline}</h1>
        {recap.images?.length > 0 && recap.images[0] && (
          <div className="newspaper-recap-photo-wrapper mb-3">
            <img src={recap.images[0]} alt="From the game" className="newspaper-recap-photo" />
            <div className="newspaper-recap-photo-caption">Photo from the game</div>
          </div>
        )}
        <div className="newspaper-body" style={{ whiteSpace: 'pre-wrap' }}>
          {recap.body}
        </div>
        {recap.fanQuote?.displayName && recap.fanQuote?.text && (
          <div className="newspaper-fan-quote">
            <div className="newspaper-fan-quote-title">From the crowd</div>
            <blockquote className="newspaper-fan-quote-text">
              &ldquo;{recap.fanQuote.text}&rdquo;
            </blockquote>
            <div className="newspaper-fan-quote-attr">— {recap.fanQuote.displayName}</div>
          </div>
        )}
        {recap.gladiatorOfGame?.playerName && (
          <div className="newspaper-gladiator-callout">
            {recap.gladiatorOfGame.photoUrl && (
              <div className="newspaper-gladiator-callout-photo-wrap">
                <img
                  src={recap.gladiatorOfGame.photoUrl}
                  alt={recap.gladiatorOfGame.playerName}
                  className="newspaper-gladiator-callout-photo"
                />
              </div>
            )}
            <div className="newspaper-gladiator-callout-text">
              <div className="newspaper-gladiator-callout-title">Gladiator of the Game</div>
              <div className="newspaper-gladiator-callout-name">{recap.gladiatorOfGame.playerName}</div>
              {recap.gladiatorOfGame.reason && (
                <div className="newspaper-gladiator-callout-reason">{recap.gladiatorOfGame.reason}</div>
              )}
            </div>
          </div>
        )}
        {(showShare || footer) && (
          <div style={{ textAlign: 'center' }}>
            {showShare && (
              <button type="button" className="newspaper-share-btn me-2" onClick={handleShare}>
                Share This Story
              </button>
            )}
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export { formatNewspaperDate };
